/**
 * Shared analytics constants for GA4 tracking and dashboard creation
 * This file ensures consistency between frontend event tracking and Grafana dashboard queries
 */

import { GameId, GAME_METADATAS } from "@mental-mint/shared-types";

// Game identifiers - use existing GameId enum and extend with additional games
export const GAME_IDS = {
  // From existing GameId enum
  TILE_CLICKER: GameId.TileClicker,
  WITOCRACY: GameId.BanterBattle, // Note: BanterBattle maps to "witocracy"
  WORD_DRIFT: GameId.Taptionary,
} as const;

// Game paths - used for filtering in GA4
export const GAME_PATHS = {
  TILE_CLICKER: `/games/${GAME_IDS.TILE_CLICKER}`,
  WITOCRACY: `/games/${GAME_IDS.WITOCRACY}`,
  WORD_DRIFT: `/games/${GAME_IDS.WORD_DRIFT}`,
} as const;

// Environment identifiers - based on actual game URLs from manifest
export const ENVIRONMENTS = {
  PRODUCTION: 'production',
  DEVELOPMENT: 'development'
} as const;

// Event names used in GA4 tracking - should match exactly between frontend and dashboard
export const EVENT_NAMES = {
  PAGE_VIEW: 'page_view',
  PAGE_LOAD: 'page_load',
  DAILY_PUZZLE_START: 'daily_puzzle_start',
  DAILY_PUZZLE_COMPLETE: 'daily_puzzle_complete',
  SHARE_GAME: 'share_game',
  SESSION_START: 'session_start',
  ERROR_OCCURRED: 'error_occurred',
  EXCEPTION: 'exception',
  
  // Conversion events (in order of typical user flow)
  DISPLAY_DAILY_PUZZLES: 'display_daily_puzzles',
  INTERACT_WITH_DAILY_PUZZLES: 'interact_with_daily_puzzles',
  COMPLETE_DAILY_PUZZLES: 'complete_daily_puzzles',
  SELECT_ARCHIVED_PUZZLE: 'select_archived_puzzle',
  BUTTON_CLICK_SHARE: 'button_click_share',
} as const;

// Event categories
export const EVENT_CATEGORIES = {
  GENERAL: 'general',
  ERRORS: 'errors',
  CONVERSION: 'conversion',
} as const;

// Analytics logging prefix for consistent console output
export const ANALYTICS_LOG_PREFIX = '[Analytics]';

// Monitoring event types - used for CloudWatch metrics tracking
export const MONITORING_EVENT_TYPES = {
  ERROR: 'error',
  PERFORMANCE: 'performance',
  PAGEVIEW: 'pageview'
} as const;

// GA4 Dimensions used in queries
export const GA4_DIMENSIONS = {
  PAGE_PATH: 'pagePath',
  PAGE_LOCATION: 'pageLocation',
  EVENT_NAME: 'eventName',
  DATE: 'date',
  SESSION_SOURCE: 'sessionSource',
  SESSION_MEDIUM: 'sessionMedium',
  COHORT_NTH_DAY: 'cohortNthDay',
  EXCEPTION_DESCRIPTION: 'exceptionDescription',
  HOSTNAME: 'hostName',
  ENVIRONMENT: 'customEvent:environment'  // Custom dimension for environment
} as const;

// GA4 Metrics used in queries
export const GA4_METRICS = {
  ACTIVE_USERS: 'activeUsers',
  NEW_USERS: 'newUsers',
  USER_ENGAGEMENT_DURATION: 'userEngagementDuration',
  EVENT_COUNT: 'eventCount',
  COHORT_ACTIVE_USERS: 'cohortActiveUsers',
  COHORT_TOTAL_USERS: 'cohortTotalUsers'
} as const;

// Dashboard template variable names
export const TEMPLATE_VARIABLES = {
  ENVIRONMENT: 'environment',
  GAME_ID: 'game_id'
} as const;

// Helper functions to get values as arrays for dashboard configuration
export const getAllGamePaths = (): string[] => Object.values(GAME_PATHS);
export const getAllGameIds = (): string[] => Object.values(GAME_IDS);
export const getAllEnvironments = (): string[] => Object.values(ENVIRONMENTS);

// Helper to get all GameId enum values (for React app games)
export const getReactAppGameIds = (): string[] => Object.values(GameId);

// Helper to get game path from game ID
export const getGamePathFromId = (gameId: string): string => {
  const pathEntry = Object.entries(GAME_PATHS).find(([key, path]) => 
    path.includes(gameId)
  );
  return pathEntry ? pathEntry[1] : `/games/${gameId}`;
};
